import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

/**
 * PTSachNrSidePanel — "More Info" panel for PTSachNr rows.
 * Matches the look-and-feel of VFFDataTableSidePanel but read-only:
 * shows static row details including Part Entry Date from supply_type_frontend_dev.
 */
const PTSachNrSidePanel = ({ isOpen, onClose, selectedRow }) => {
	const [isHovered, setIsHovered] = useState(false);
	const [isCollapsed, setIsCollapsed] = useState(false);

	// Reset collapse state whenever a new row is selected
	useEffect(() => {
		if (selectedRow) {
			setIsCollapsed(false);
		}
	}, [selectedRow]);

	// Close on click outside the panel
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (isOpen && !event.target.closest(".ptsachnr-side-panel-container")) {
				onClose();
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [isOpen, onClose]);

	// Close on Escape
	useEffect(() => {
		const handleEscapeKey = (event) => {
			if (event.key === "Escape" && isOpen) {
				onClose();
			}
		};
		document.addEventListener("keydown", handleEscapeKey);
		return () => document.removeEventListener("keydown", handleEscapeKey);
	}, [isOpen, onClose]);

	if (!isOpen || !selectedRow) return null;

	const toggleCollapse = (e) => {
		e.stopPropagation();
		setIsCollapsed((prev) => !prev);
	};

	const handleClose = (e) => {
		e.stopPropagation();
		onClose();
	};

	const handleCollapsedClick = () => {
		if (isCollapsed) setIsCollapsed(false);
	};

	/** Format a raw date string (YYYY-MM-DD or ISO) to DD/MM/YYYY for display. */
	const formatDate = (raw) => {
		if (!raw) return "N/A";
		try {
			const d = new Date(raw);
			if (isNaN(d.getTime())) return raw;
			return d.toLocaleDateString("en-GB", {
				day: "2-digit",
				month: "2-digit",
				year: "numeric",
			});
		} catch {
			return raw;
		}
	};

	const styles = {
		container: {
			position: "absolute",
			top: 0,
			right: 0,
			width: isCollapsed ? "50px" : "360px",
			height: "calc(100% - 47px)",
			backgroundColor: isCollapsed && isHovered ? "#e8e8e8" : "#f0f0f0",
			border: "2px solid #1e2832",
			borderRight: "none",
			borderTop: "none",
			borderBottom: "none",
			boxShadow: "-4px 0 12px rgba(0, 0, 0, 0.15)",
			transition: "all 0.3s ease",
			zIndex: 1000,
			overflow: "hidden",
			fontFamily: "MAN EUROPE_Regular",
			cursor: isCollapsed ? "pointer" : "default",
			display: "flex",
			flexDirection: "column",
		},
		header: {
			display: "flex",
			alignItems: "center",
			padding: "12px",
			backgroundColor: "#1e2832",
			color: "white",
			justifyContent: isCollapsed ? "center" : "space-between",
			borderBottom: "1px solid #ddd",
			minHeight: "48px",
			flexShrink: 0,
		},
		title: {
			fontSize: "16px",
			fontFamily: "MAN EUROPE_Bold",
			margin: 0,
			display: isCollapsed ? "none" : "block",
		},
		toggleButton: {
			background: "none",
			border: "none",
			cursor: "pointer",
			padding: "4px",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			borderRadius: "4px",
			color: "white",
		},
		closeButton: {
			background: "none",
			border: "none",
			cursor: "pointer",
			padding: "4px",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			borderRadius: "4px",
			color: "white",
			marginLeft: "8px",
		},
		content: {
			opacity: isCollapsed ? 0 : 1,
			visibility: isCollapsed ? "hidden" : "visible",
			transition: "opacity 0.2s ease",
			padding: isCollapsed ? 0 : "16px",
			flex: 1,
			overflowY: "auto",
			overflowX: "hidden",
		},
		infoGroup: {
			marginBottom: "14px",
			borderBottom: "1px solid #e0e0e0",
			paddingBottom: "10px",
		},

		infoLabel: {
			fontSize: "11px",
			fontFamily: "MAN EUROPE_Bold",
			color: "#666",
			marginBottom: "3px",
			textTransform: "uppercase",
			letterSpacing: "0.5px",
		},

		infoValue: {
			fontSize: "13px",
			fontFamily: "MAN EUROPE_Regular",
			color: "#333",
			wordBreak: "break-word",
			lineHeight: "1.4",
		},

	};

	// Helper to display N/A when value is empty / dash
	const val = (v) => {
		if (v === null || v === undefined || v === "" || v === "-") return "N/A";
		return v;
	};

	return (
		<div
			className="ptsachnr-side-panel-container"
			data-tour-target="ptsachnr-sidepanel"
			style={styles.container}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onClick={handleCollapsedClick}
		>
			{/* Header */}
			<div style={styles.header}>
				{!isCollapsed && <h3 style={styles.title}>More Info</h3>}
				<div style={{ display: "flex", alignItems: "center" }}>
					<button
						style={styles.toggleButton}
						onClick={toggleCollapse}
						title={isCollapsed ? "Expand panel" : "Collapse panel"}
						aria-label={isCollapsed ? "Expand panel" : "Collapse panel"}
					>
						{isCollapsed ? (
							<ChevronLeft style={{ width: "20px", height: "20px" }} />
						) : (
							<ChevronRight style={{ width: "20px", height: "20px" }} />
						)}
					</button>
					{!isCollapsed && (
						<button
							style={styles.closeButton}
							onClick={handleClose}
							title="Close panel"
							aria-label="Close panel"
							data-tour-target="ptsachnr-sidepanel-close"
						>
							<X style={{ width: "16px", height: "16px" }} />
						</button>
					)}
				</div>
			</div>

			{/* Content */}
			<div
				style={styles.content}
				onClick={(e) => !isCollapsed && e.stopPropagation()}
			>
				{/* Part Entry Date */}
				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Part Entry Date</div>
					<div style={styles.infoValue}>
						{formatDate(selectedRow?.part_entry_date)}
					</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>PTSNR / VMSNR</div>
					<div style={styles.infoValue}>
						{val(selectedRow?.vmsnr || selectedRow?.ptsnr)}
					</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>SNR</div>
					<div style={styles.infoValue}>
						{val(selectedRow?.snr_kurz || selectedRow?.snr)}
					</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Description</div>
					<div style={styles.infoValue}>{val(selectedRow?.description)}</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Project</div>
					<div style={styles.infoValue}>
						{val(selectedRow?.projekt || selectedRow?.project)}
					</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Project Name</div>
					<div style={styles.infoValue}>{val(selectedRow?.programm)}</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>SE Team</div>
					<div style={styles.infoValue}>{val(selectedRow?.se_teams)}</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Supplier</div>
					<div style={styles.infoValue}>{val(selectedRow?.supplier)}</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Purchase Type (BY SET)</div>
					<div style={styles.infoValue}>{val(selectedRow?.purchase_type)}</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Purchase Type (BY SYSTEM)</div>
					<div style={styles.infoValue}>
						{val(selectedRow?.purchase_type_system)}
					</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>GX Classification</div>
					<div style={styles.infoValue}>
						{val(selectedRow?.gx_classification)}
					</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>ALG / Ramp-up Group</div>
					<div style={styles.infoValue}>{val(selectedRow?.ramp_up_group)}</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Series Annual Vol (Total Req)</div>
					<div style={styles.infoValue}>{val(selectedRow?.total_req)}</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>FET</div>
					<div style={styles.infoValue}>{val(selectedRow?.fet)}</div>
				</div>

				<div style={styles.infoGroup}>
					<div style={styles.infoLabel}>Change No</div>
					<div style={styles.infoValue}>
						{val(selectedRow?.aenderungsnummer)}
					</div>
				</div>

				<div style={{ ...styles.infoGroup, borderBottom: "none", marginBottom: 0 }}>
					<div style={styles.infoLabel}>Category</div>
					<div style={styles.infoValue}>{val(selectedRow?.category)}</div>
				</div>
			</div>
		</div>
	);
};

export default PTSachNrSidePanel;
